import * as THREE from 'three';

export const AIRSTRIKE_MAX = 2;
const MARK_RANGE = 80;
const STRIKE_DELAY = 2.5;
const STRIKE_COUNT = 3;
const STRIKE_SPREAD = 8;
const BLAST_DAMAGE = 55;
const BLAST_RADIUS = 4.5;

type PendingStrike = {
  center: THREE.Vector3;
  lineDir: THREE.Vector3;
  delay: number;
  marker: THREE.Group;
};

export type AirstrikeBlast = {
  x: number;
  y: number;
  z: number;
  damage: number;
  radius: number;
};

/** Aim crosshair, mark ground target, delayed line of 3 blasts with red smoke marker. */
export class FpsAirstrike {
  private count = AIRSTRIKE_MAX;
  private pending: PendingStrike[] = [];
  private readonly raycaster = new THREE.Raycaster();
  private readonly smokeGeo = new THREE.CylinderGeometry(0.35, 1.8, 3.2, 10, 1, true);
  private readonly smokeMat = new THREE.MeshBasicMaterial({
    color: 0xcc2222,
    transparent: true,
    opacity: 0.62,
    depthWrite: false,
  });
  private readonly ringGeo = new THREE.RingGeometry(0.6, 2.4, 24);
  private readonly ringMat = new THREE.MeshBasicMaterial({
    color: 0xff4444,
    transparent: true,
    opacity: 0.75,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  constructor(private readonly sceneRoot: THREE.Group) {}

  getCount(): number {
    return this.count;
  }

  /** Bot/neutral strike — does not consume player inventory. */
  queueExternalStrike(x: number, z: number, y = 0): void {
    const center = new THREE.Vector3(x, y, z);
    const lineDir = new THREE.Vector3(Math.cos(x * 0.17 + z * 0.11), 0, Math.sin(x * 0.13 - z * 0.09));
    if (lineDir.lengthSq() < 1e-6) lineDir.set(1, 0, 0);
    else lineDir.normalize();

    const marker = this.createSmokeMarker(center);
    this.sceneRoot.add(marker);
    this.pending.push({
      center,
      lineDir,
      delay: STRIKE_DELAY,
      marker,
    });
  }

  /** Add strikes from loot; capped at AIRSTRIKE_MAX. */
  addCount(n: number): number {
    const before = this.count;
    this.count = Math.min(AIRSTRIKE_MAX, this.count + n);
    return this.count - before;
  }

  /** Raycast from crosshair to mark strike (max 80m). Returns false if empty. */
  tryMarkTarget(camera: THREE.Camera): boolean {
    if (this.count <= 0) return false;

    const origin = new THREE.Vector3();
    const look = new THREE.Vector3();
    camera.getWorldPosition(origin);
    camera.getWorldDirection(look);

    this.raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
    this.raycaster.far = MARK_RANGE;
    const hits = this.raycaster.intersectObject(this.sceneRoot, true);
    const hit = hits[0];
    const center = hit
      ? hit.point.clone()
      : origin.clone().addScaledVector(look, MARK_RANGE);

    const lineDir = new THREE.Vector3(-look.z, 0, look.x);
    if (lineDir.lengthSq() < 1e-6) lineDir.set(1, 0, 0);
    else lineDir.normalize();

    const marker = this.createSmokeMarker(center);
    this.sceneRoot.add(marker);

    this.pending.push({
      center,
      lineDir,
      delay: STRIKE_DELAY,
      marker,
    });
    this.count -= 1;
    return true;
  }

  update(dt: number, onBlast: (blast: AirstrikeBlast) => void): void {
    for (let i = this.pending.length - 1; i >= 0; i--) {
      const strike = this.pending[i]!;
      const wasAbove = strike.delay > 0;
      strike.delay -= dt;

      const u = 1 - Math.max(0, strike.delay / STRIKE_DELAY);
      const pulse = 1 + Math.sin(performance.now() * 0.006) * 0.12;
      strike.marker.scale.set(pulse, 1 + u * 0.35, pulse);
      strike.marker.rotation.y += dt * 1.6;
      strike.marker.traverse((obj) => {
        if (!(obj instanceof THREE.Mesh)) return;
        const mat = obj.material as THREE.MeshBasicMaterial;
        if (mat.transparent) mat.opacity = 0.45 + u * 0.35 + Math.sin(performance.now() * 0.008) * 0.08;
      });

      if (wasAbove && strike.delay <= 0) {
        this.fireLine(strike, onBlast);
        this.removeMarker(strike.marker);
        this.pending.splice(i, 1);
      }
    }
  }

  private fireLine(strike: PendingStrike, onBlast: (blast: AirstrikeBlast) => void): void {
    const half = STRIKE_SPREAD * 0.5;
    const offsets = [-half, 0, half];
    for (const off of offsets) {
      const x = strike.center.x + strike.lineDir.x * off;
      const z = strike.center.z + strike.lineDir.z * off;
      onBlast({
        x,
        y: strike.center.y,
        z,
        damage: BLAST_DAMAGE,
        radius: BLAST_RADIUS,
      });
    }
  }

  private createSmokeMarker(center: THREE.Vector3): THREE.Group {
    const group = new THREE.Group();
    group.position.copy(center);
    group.position.y += 0.05;

    const smoke = new THREE.Mesh(this.smokeGeo, this.smokeMat.clone());
    smoke.position.y = 1.6;
    group.add(smoke);

    const ring = new THREE.Mesh(this.ringGeo, this.ringMat.clone());
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.08;
    group.add(ring);

    const inner = new THREE.Mesh(
      new THREE.CircleGeometry(0.55, 16),
      new THREE.MeshBasicMaterial({
        color: 0xff2222,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    inner.rotation.x = -Math.PI / 2;
    inner.position.y = 0.1;
    group.add(inner);

    return group;
  }

  private removeMarker(marker: THREE.Group): void {
    marker.removeFromParent();
    marker.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      if (obj.geometry !== this.smokeGeo && obj.geometry !== this.ringGeo) {
        obj.geometry.dispose();
      }
      (obj.material as THREE.Material).dispose();
    });
  }

  dispose(): void {
    for (const strike of this.pending) this.removeMarker(strike.marker);
    this.pending.length = 0;
    this.smokeGeo.dispose();
    this.smokeMat.dispose();
    this.ringGeo.dispose();
    this.ringMat.dispose();
  }
}
